'use strict';

/**
 * Project Funding — host view (funding-config setup grid + accounting overview + line-item funding).
 *
 * Standalone project-scoped screen (front-door picker + deep link #/Project/Funding/:ID), mirroring the
 * ImportantFiles / Sample Plan shell. The funding-config setup grid is a nested table (Category → Source → Fund
 * buckets) with inline-editable bucket fields (saved via the provider's Finance …/Buckets call), searchable
 * add-pickers off the lazily-loaded finance profile, and per-bucket remove. The accounting overview renders the
 * digest totals + the funding-solution outcomes and violations. A full-width line-item funding panel assigns
 * each line item to one of the project's funding categories (uncategorized flagged). Simulation and the
 * purchase-order export arrive in later slices.
 */

const libPictView = require('pict-view');

const html = String.raw;
const fEsc = (pValue) => String(pValue == null ? '' : pValue).replace(/[&<>]/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[pChar]));
const fAttr = (pValue) => String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar]));

const CURRENCY = (typeof Intl !== 'undefined') ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }) : null;
const fMoney = (pValue) => { const tmpN = Number(pValue); if (!Number.isFinite(tmpN)) { return '—'; } return CURRENCY ? CURRENCY.format(tmpN) : ('$' + tmpN.toFixed(2)); };
const fPct = (pValue) => { const tmpN = Number(pValue); if (!Number.isFinite(tmpN)) { return '0%'; } return (tmpN * 100).toFixed(1) + '%'; };   // API sends fractions (0.3333)

const GRID_MOUNT = '#ProjectFunding-Grid';
const ACCT_MOUNT = '#ProjectFunding-Accounting';

// Above this many line items we fall back from per-row Pict-Section-Pickers to native <select>s — mounting several
// hundred picker views at once is heavy; native dropdowns keep a very large project's funding panel responsive.
const LI_PICKER_CAP = 400;

// Source-bucket editable fields (label + numeric hint). EffectiveWeight is computed → read-only, shown separately.
const SOURCE_FIELDS =
[
	{ Key: 'Priority', Label: 'Priority', Kind: 'int' },
	{ Key: 'PriorityWeight', Label: 'Weight', Kind: 'num' },
	{ Key: 'ProjectFundCap', Label: 'Project Cap', Kind: 'money' },
	{ Key: 'FundCap', Label: 'Fund Cap', Kind: 'money' },
	{ Key: 'PurchaseOrderItem', Label: 'PO Item', Kind: 'text' },
];
const FUND_FIELDS =
[
	{ Key: 'Sequence', Label: 'Sequence', Kind: 'int' },
	{ Key: 'Weight', Label: 'Weight', Kind: 'num' },
	{ Key: 'PurchaseOrderItem', Label: 'PO Item', Kind: 'text' },
];

class ProjectFunding extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this._projectFilterTimer = null;
		this._projectLoadToken = 0;
		this._pickTimer = null;
		this._pickToken = 0;
		// When embedded in a host (the ProjectWorkspace Contract ▸ Funding tab) this holds that host selector, and
		// every render — including provider-triggered repaints — targets it instead of the app container.
		this._embedDestination = null;
		// Accounting-overview panel collapse state (it sits full-width above the setup grid).
		this._acctCollapsed = false;
	}

	get provider() { return this.pict.providers['ProjectFunding']; }
	get modal() { return this.pict.views['Pict-Section-Modal']; }

	/** Render into the embed host when embedded (so provider `_render()` repaints land in the Contract ▸ Funding
	 *  tab, not the app container). An explicit destination argument always wins. */
	render(pRenderableHash, pRenderDestinationAddress, pTemplateRecordAddress, pRootRenderable)
	{
		const tmpDestination = (typeof pRenderDestinationAddress === 'string') ? pRenderDestinationAddress : (this._embedDestination || undefined);
		return super.render(pRenderableHash, tmpDestination, pTemplateRecordAddress, pRootRenderable);
	}

	/** Embed the funding dashboard into a host element for a fixed project (skips the front-door picker). The
	 *  ProjectWorkspace Contract ▸ Funding tab calls this after each paint; it reloads only when the project differs. */
	openInto(pIDProject, pHostSelector)
	{
		this._embedDestination = pHostSelector;
		const tmpState = (this.pict.AppData && this.pict.AppData.ProjectFunding) || {};
		if (this.provider && (!tmpState.HasModel || (Number(this.provider.idProject) !== Number(pIDProject))))
		{
			this.provider.loadAsync(pIDProject);   // provider._render → this.render → the host
		}
		else { this.render(); }   // already loaded for this project — just (re)paint into the host
	}

	/** Leave embedded mode — the standalone #/Project/Funding screen renders to the app container again. */
	exitEmbed() { this._embedDestination = null; }

	/** Collapse / expand the (full-width, top) Accounting overview panel. Toggles the DOM in place (no full
	 *  re-render needed — the content is already painted, just hidden) and keeps the persisted flag in sync. */
	toggleAccounting()
	{
		this._acctCollapsed = !this._acctCollapsed;
		const tmpState = (this.pict.AppData && this.pict.AppData.ProjectFunding) || {};
		tmpState.AcctClass = this._acctCollapsed ? 'is-collapsed' : '';
		tmpState.AcctChevron = this._acctCollapsed ? 'fa-chevron-down' : 'fa-chevron-up';
		const tmpPanel = document.querySelector('.pf-acct-panel');
		if (tmpPanel) { tmpPanel.classList.toggle('is-collapsed', this._acctCollapsed); }
		const tmpCaret = document.querySelector('.pf-acct-caret i');
		if (tmpCaret) { tmpCaret.className = 'fas ' + (this._acctCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'); }
	}
	get pickerProvider() { return this.pict.providers && this.pict.providers['Pict-Section-Picker']; }
	_toast(pMessage, pType, pDuration) { if (this.modal && (typeof this.modal.toast === 'function')) { const tmpOptions = { type: pType || 'info' }; if (pDuration !== undefined) { tmpOptions.duration = pDuration; } this.modal.toast(pMessage, tmpOptions); } }

	onBeforeRender(pRenderable)
	{
		const tmpState = (this.pict.AppData && this.pict.AppData.ProjectFunding) || {};
		tmpState.LoadingSlot = tmpState.Loading ? [ tmpState ] : [];
		tmpState.ErrorSlot = (!tmpState.Loading && tmpState.Message) ? [ tmpState ] : [];
		tmpState.PickerSlot = (!tmpState.Loading && !tmpState.Message && !tmpState.HasModel) ? [ tmpState ] : [];
		tmpState.BodySlot = (!tmpState.Loading && tmpState.HasModel) ? [ tmpState ] : [];
		tmpState.ManageSlot = (tmpState.HasModel && !tmpState.Loading && tmpState.CanManage) ? [ tmpState ] : [];
		tmpState.ReadOnlySlot = (tmpState.HasModel && !tmpState.Loading && !tmpState.CanManage) ? [ tmpState ] : [];
		tmpState.TitleText = (tmpState.HasModel && tmpState.ProjectName) ? `Funding — ${tmpState.ProjectName}` : 'Project Funding';
		tmpState.EmbedClass = this._embedDestination ? 'pf-embedded' : '';
		// "Choose another project" only makes sense on the standalone screen — embedded in a project's Contract tab
		// the project is already fixed, so suppress it.
		tmpState.ChangeProjectSlot = (!this._embedDestination && tmpState.HasModel && !tmpState.Loading) ? [ tmpState ] : [];
		// Accounting overview collapse state (persisted across full re-renders).
		tmpState.AcctClass = this._acctCollapsed ? 'is-collapsed' : '';
		tmpState.AcctChevron = this._acctCollapsed ? 'fa-chevron-down' : 'fa-chevron-up';
		this.pict.AppData.ProjectFunding = tmpState;
		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable)
	{
		const tmpState = (this.pict.AppData && this.pict.AppData.ProjectFunding) || {};
		if (tmpState.HasModel && !tmpState.Loading) { this.renderHeaderTools(); this.renderGrid(); this.renderAccounting(); this.renderLineItems(); }
		else if (tmpState.PickerSlot && tmpState.PickerSlot.length) { this._initProjectPicker(); }
		this.pict.CSSMap.injectCSS();
		return super.onAfterRender(pRenderable);
	}

	// --- Front door -------------------------------------------------------------------------------

	_initProjectPicker()
	{
		const tmpMount = this.pict.ContentAssignment.getElement('#ProjectFunding-ProjectList');
		if (!tmpMount || (tmpMount.length === 0)) { return; }
		const tmpInput = document.getElementById('ProjectFunding-ProjectFilter');
		this._loadProjects(tmpInput ? tmpInput.value : '');
	}

	onProjectFilter(pValue)
	{
		if (this._projectFilterTimer) { clearTimeout(this._projectFilterTimer); }
		this._projectFilterTimer = setTimeout(() => { this._loadProjects(pValue); }, 200);
	}

	_loadProjects(pTerm)
	{
		if (!this.provider) { return; }
		const tmpToken = (this._projectLoadToken = (this._projectLoadToken || 0) + 1);
		this.provider.searchProjectsAsync(pTerm).then((pProjects) =>
		{
			if (tmpToken !== this._projectLoadToken) { return; }
			const tmpList = Array.isArray(pProjects) ? pProjects : [];
			if (!tmpList.length) { this.pict.ContentAssignment.assignContent('#ProjectFunding-ProjectList', '<div class="pf-note">No projects match.</div>'); return; }
			const tmpRows = tmpList.map((pProject) =>
				`<button type="button" class="pf-proj-row" onclick="_Pict.views.ProjectFunding.openProject(${Number(pProject.IDProject)})"><span>${fEsc(pProject.Name)}</span><i class="fas fa-chevron-right"></i></button>`).join('');
			this.pict.ContentAssignment.assignContent('#ProjectFunding-ProjectList', tmpRows + (tmpList.length >= 100 ? '<div class="pf-note">Showing the first 100 — type to narrow.</div>' : ''));
		}).catch(() => { if (tmpToken === this._projectLoadToken) { this.pict.ContentAssignment.assignContent('#ProjectFunding-ProjectList', '<div class="pf-note pf-err">Could not load projects.</div>'); } });
	}

	openProject(pIDProject) { const tmpID = Number(pIDProject); if (Number.isFinite(tmpID) && (tmpID > 0)) { window.location.hash = `#/Project/Funding/${tmpID}`; } }
	chooseAnotherProject() { window.location.hash = '#/Project/Funding'; }

	// --- Setup grid -------------------------------------------------------------------------------

	renderGrid()
	{
		const tmpMount = this.pict.ContentAssignment.getElement(GRID_MOUNT);
		if (!tmpMount || (tmpMount.length === 0)) { return; }
		const tmpConfig = this.provider.config;
		const tmpManage = !!(this.pict.AppData.ProjectFunding && this.pict.AppData.ProjectFunding.CanManage);
		if (!tmpConfig.length)
		{
			this.pict.ContentAssignment.assignContent(GRID_MOUNT, `<div class="pf-empty"><i class="fas fa-sitemap"></i><p>No funding is configured for this project yet.</p>${tmpManage ? '<button class="button is-link is-small" onclick="_Pict.views.ProjectFunding.addCategory()"><span class="icon"><i class="fas fa-plus"></i></span><span>Add a funding category</span></button>' : ''}</div>`);
			return;
		}
		this.pict.ContentAssignment.assignContent(GRID_MOUNT, tmpConfig.map((pCat) => this._categoryHTML(pCat, tmpManage)).join(''));
	}

	_categoryHTML(pCat, pManage)
	{
		const tmpKey = fAttr(pCat.Key);
		const tmpSources = pCat.FundingSourceBuckets || [];
		const tmpActions = pManage
			? `<span class="pf-cat-actions"><button class="pf-btn" title="Add funding source" onclick="_Pict.views.ProjectFunding.addSource('${tmpKey}')"><i class="fas fa-plus"></i> Source</button>`
				+ `<button class="pf-btn pf-btn-danger" title="Remove this category and everything under it" onclick="_Pict.views.ProjectFunding.removeBucket('${tmpKey}','category')"><i class="fas fa-trash"></i></button></span>`
			: '';
		const tmpBody = tmpSources.length
			? `<div class="pf-src-tablewrap"><table class="pf-src-table"><thead><tr><th>Funding Source</th>${SOURCE_FIELDS.map((f) => `<th>${fEsc(f.Label)}</th>`).join('')}<th title="Computed">Eff. Wt.</th>${pManage ? '<th></th>' : ''}</tr></thead><tbody>${tmpSources.map((pSrc) => this._sourceRowsHTML(pSrc, pManage)).join('')}</tbody></table></div>`
			: '<div class="pf-cat-empty">No funding sources yet.</div>';
		return `<div class="pf-cat"><div class="pf-cat-head"><i class="fas fa-folder-tree"></i><span class="pf-cat-name">${fEsc(pCat.Name)}</span>${pCat.Code ? `<span class="pf-tag">${fEsc(pCat.Code)}</span>` : ''}${pCat.Type ? `<span class="pf-tag pf-tag-type">${fEsc(pCat.Type)}</span>` : ''}${tmpActions}</div>${tmpBody}</div>`;
	}

	_sourceRowsHTML(pSrc, pManage)
	{
		const tmpKey = fAttr(pSrc.Key);
		const tmpFunds = pSrc.FundBuckets || [];
		const tmpCells = SOURCE_FIELDS.map((f) => `<td>${this._editCell(tmpKey, f, pSrc[f.Key], pManage)}</td>`).join('');
		const tmpAct = pManage
			? `<td class="pf-row-act"><button class="pf-btn" title="Add fund" onclick="_Pict.views.ProjectFunding.addFund('${tmpKey}')"><i class="fas fa-plus"></i> Fund</button><button class="pf-btn pf-btn-danger" title="Remove source" onclick="_Pict.views.ProjectFunding.removeBucket('${tmpKey}','source')"><i class="fas fa-xmark"></i></button></td>`
			: '';
		let tmpHTML = `<tr class="pf-src-row"><td class="pf-src-name">${fEsc(pSrc.Name)}</td>${tmpCells}<td class="pf-eff" data-eff="${tmpKey}">${fPct(pSrc.EffectiveWeight)}</td>${tmpAct}</tr>`;
		// Funds nested under the source
		tmpFunds.forEach((pFund) =>
		{
			const tmpFKey = fAttr(pFund.Key);
			const tmpFCells = FUND_FIELDS.map((f) => `<td>${this._editCell(tmpFKey, f, pFund[f.Key], pManage)}</td>`).join('');
			const tmpFAct = pManage ? `<td class="pf-row-act"><button class="pf-btn pf-btn-danger" title="Remove fund" onclick="_Pict.views.ProjectFunding.removeBucket('${tmpFKey}','fund')"><i class="fas fa-xmark"></i></button></td>` : '';
			// pad the fund row to align under the source columns (fund has fewer editable fields)
			const tmpPad = SOURCE_FIELDS.length - FUND_FIELDS.length;
			tmpHTML += `<tr class="pf-fund-row"><td class="pf-fund-name"><i class="fas fa-turn-up fa-rotate-90"></i> ${fEsc(pFund.Name)}${(pFund.Balance != null) ? ` <span class="pf-fund-bal">${fMoney(pFund.Balance)}</span>` : ''}</td>${tmpFCells}${'<td></td>'.repeat(Math.max(0, tmpPad))}<td></td>${tmpFAct}</tr>`;
		});
		return tmpHTML;
	}

	_editCell(pBucketKey, pField, pValue, pManage)
	{
		const tmpVal = (pValue == null) ? '' : String(pValue);
		if (!pManage) { return `<span class="pf-ro">${fEsc(pField.Kind === 'money' ? fMoney(tmpVal) : tmpVal)}</span>`; }
		const tmpType = (pField.Kind === 'text') ? 'text' : 'number';
		const tmpStep = (pField.Kind === 'int') ? '1' : (pField.Kind === 'money' || pField.Kind === 'num') ? 'any' : '';
		const tmpMin = (pField.Kind === 'text') ? '' : ' min="0"';
		return `<input class="pf-inp pf-inp-${pField.Kind}" type="${tmpType}"${tmpStep ? ` step="${tmpStep}"` : ''}${tmpMin} value="${fAttr(tmpVal)}" data-bucket="${fAttr(pBucketKey)}" data-field="${fAttr(pField.Key)}" data-kind="${fAttr(pField.Kind)}" onchange="_Pict.views.ProjectFunding.onFieldEdit(this)" />`;
	}

	async onFieldEdit(pEl)
	{
		if (!pEl) { return; }
		const tmpKey = pEl.getAttribute('data-bucket');
		const tmpField = pEl.getAttribute('data-field');
		const tmpKind = pEl.getAttribute('data-kind');
		const tmpValue = pEl.value;
		// Never persist an empty/invalid number — the funding engine does Big() math on these fields. Revert instead.
		if ((tmpKind !== 'text') && ((tmpValue === '') || !Number.isFinite(Number(tmpValue))))
		{
			const tmpBucket = this.provider.findBucket(tmpKey);
			pEl.value = (tmpBucket && tmpBucket.node && (tmpBucket.node[tmpField] != null)) ? String(tmpBucket.node[tmpField]) : '';
			return;
		}
		pEl.disabled = true;
		try { await this.provider.saveBucketAsync(tmpKey, { [tmpField]: tmpValue }); }
		catch (pError) { this._toast((pError && pError.message) ? pError.message : 'Could not save that change.', 'error', 6000); }
		finally { pEl.disabled = false; }
	}

	/** After a single bucket-field save (the provider's "quiet" path): refresh the accounting panel and patch the
	 *  computed Effective-Weight cells in place — WITHOUT rebuilding the grid, so the user's focus + any in-progress
	 *  edit in another cell survive (the grid inputs are keyed by data-bucket, not array position). */
	afterFieldSave()
	{
		this.renderAccounting();
		const tmpConfig = this.provider.config;
		const fEscKey = (pK) => ((window.CSS && CSS.escape) ? CSS.escape(pK) : pK);
		tmpConfig.forEach((pCat) => (pCat.FundingSourceBuckets || []).forEach((pSrc) =>
		{
			const tmpCell = document.querySelector(`.pf-eff[data-eff="${fEscKey(pSrc.Key)}"]`);
			if (tmpCell) { tmpCell.textContent = fPct(pSrc.EffectiveWeight); }
		}));
	}

	// --- Add flows (searchable pickers off the lazy finance profile) --------------------------------

	addCategory() { this._pickFromProfile('Add a funding category', 'FundingCategories', 'category', (pItem) => this.provider.addCategoryAsync(pItem), this.provider.config.map((pCat) => String(pCat.IDFundingCategory))); }

	addSource(pCategoryBucketKey)
	{
		this._pickFromProfile('Add a funding source', 'FundingSources', 'source', (pItem) => this.provider.addSourceAsync(pItem, pCategoryBucketKey));
	}

	addFund(pSourceBucketKey)
	{
		this._pickFromProfile('Add a fund', 'Funds', 'fund', (pItem) => this.provider.addFundsAsync([ pItem ], pSourceBucketKey));
	}

	/** Open a searchable modal over a (large) profile list; on pick, run pOnPick then refresh + toast. */
	async _pickFromProfile(pTitle, pListName, pNoun, pOnPick, pExcludeKeys)
	{
		this._pickList = pListName;
		this._pickExclude = new Set((pExcludeKeys || []).map(String));
		this._pickOnPick = pOnPick;
		this._pickNoun = pNoun;
		this.modal.show(
			{
				title: pTitle,
				content: `<p class="pf-modal-help">Search the customer’s ${fEsc(pNoun)} list and pick one to add.</p>`
					+ `<input id="PF-PickSearch" class="input" type="text" autocomplete="off" placeholder="Search by name…" oninput="_Pict.views.ProjectFunding.onPickSearch(this.value)" />`
					+ '<div id="PF-PickResults" class="pf-pick-list"><div class="pf-note">Loading…</div></div>',
				buttons: [ { Hash: 'close', Label: 'Close' } ],
				width: '560px',
			});
		await this.provider.loadProfileAsync();
		this.onPickSearch('');
	}

	onPickSearch(pTerm)
	{
		if (this._pickTimer) { clearTimeout(this._pickTimer); }
		const tmpToken = (this._pickToken = (this._pickToken || 0) + 1);
		this._pickTimer = setTimeout(() =>
		{
			if (tmpToken !== this._pickToken) { return; }
			this._renderPickResults(pTerm);
		}, 150);
	}

	_renderPickResults(pTerm)
	{
		const tmpHost = document.getElementById('PF-PickResults');
		if (!tmpHost) { return; }
		const tmpProfile = this.provider.profile || {};
		const tmpList = tmpProfile[this._pickList] || [];
		const tmpTerm = String(pTerm || '').trim().toLowerCase();
		const tmpMatches = [];
		for (let i = 0; (i < tmpList.length) && (tmpMatches.length < 100); i++)
		{
			const tmpItem = tmpList[i];
			if (this._pickExclude.has(String(tmpItem.Key))) { continue; }
			const tmpName = String(tmpItem.Name || '').toLowerCase();
			const tmpCode = String(tmpItem.Code || tmpItem.Hash || '').toLowerCase();
			if (!tmpTerm || (tmpName.indexOf(tmpTerm) >= 0) || (tmpCode.indexOf(tmpTerm) >= 0)) { tmpMatches.push(tmpItem); }
		}
		if (!tmpMatches.length) { tmpHost.innerHTML = '<div class="pf-note">No matches.</div>'; return; }
		tmpHost.innerHTML = tmpMatches.map((pItem, pIndex) =>
			`<button type="button" class="pf-pick-row" onclick="_Pict.views.ProjectFunding.pickResult(${pIndex})">`
			+ `<span class="pf-pick-name">${fEsc(pItem.Name)}</span>`
			+ (pItem.Type ? `<span class="pf-tag pf-tag-type">${fEsc(pItem.Type)}</span>` : '')
			+ ((pItem.Balance != null && this._pickList === 'Funds') ? `<span class="pf-pick-bal">${fMoney(pItem.Balance)}</span>` : '')
			+ '</button>').join('');
		this._pickMatches = tmpMatches;
	}

	pickResult(pIndex)
	{
		const tmpItem = (this._pickMatches || [])[pIndex];
		if (!tmpItem) { return; }
		const tmpOnPick = this._pickOnPick;
		const tmpNoun = this._pickNoun;
		if (this.modal && (typeof this.modal.dismissModals === 'function')) { this.modal.dismissModals(); }
		Promise.resolve(tmpOnPick(tmpItem))
			.then(() => this._toast(`Added ${tmpNoun} “${tmpItem.Name}”.`, 'success'))
			.catch((pError) => this._toast((pError && pError.message) ? pError.message : `Could not add the ${tmpNoun}.`, 'error', 6000));
	}

	async removeBucket(pKey, pNoun)
	{
		const tmpOk = await this.modal.confirm(`Remove this ${fEsc(pNoun || 'item')} from the project’s funding${pNoun === 'category' || pNoun === 'source' ? ' (and everything under it)' : ''}?`, { title: 'Remove funding', confirmLabel: 'Remove', cancelLabel: 'Cancel', dangerous: true });
		if (!tmpOk) { return; }
		try { await this.provider.deleteBucketsAsync([ pKey ]); this._toast('Removed.', 'success'); }
		catch (pError) { this._toast((pError && pError.message) ? pError.message : 'Could not remove that.', 'error', 6000); }
	}

	// --- Purchase-order export --------------------------------------------------------------------

	/** Header toolbar: the purchase-order export button, shown only when the customer's toggle enables it. Export is
	 *  an output action available to any viewer, so it is NOT gated on the manage permission. */
	renderHeaderTools()
	{
		const tmpMount = this.pict.ContentAssignment.getElement('#ProjectFunding-HeaderTools');
		if (!tmpMount || (tmpMount.length === 0)) { return; }
		const tmpButtons = [];
		// Simulate is a read-only what-if; it's only useful once some line items are categorized (they carry the
		// Category.Key the scenario needs), so gate the button on that.
		if (this.provider.lineItemRows().some((pRow) => !pRow.IsUncategorized))
		{
			tmpButtons.push('<button type="button" class="pf-head-btn pf-sim-btn" onclick="_Pict.views.ProjectFunding.openSimulation()"><i class="fas fa-flask"></i> Funding Sim</button>');
		}
		if (this.provider.canExportPurchaseOrder())
		{
			const tmpBusy = this._exporting ? ' disabled' : '';
			const tmpIcon = this._exporting ? 'fa-spinner fa-spin' : 'fa-file-arrow-down';
			tmpButtons.push(`<button type="button" class="pf-head-btn pf-export-btn"${tmpBusy} onclick="_Pict.views.ProjectFunding.onExportPurchaseOrder()"><i class="fas ${tmpIcon}"></i> ${this._exporting ? 'Exporting…' : 'PO Export'}</button>`);
		}
		this.pict.ContentAssignment.assignContent('#ProjectFunding-HeaderTools', tmpButtons.join(''));
	}

	async onExportPurchaseOrder()
	{
		if (this._exporting) { return; }
		this._exporting = true;
		this.renderHeaderTools();
		try
		{
			const tmpResult = await this.provider.exportPurchaseOrderAsync();
			this._showExportResult(tmpResult);
		}
		catch (pError) { this._toast((pError && pError.message) ? pError.message : 'Could not generate the purchase order.', 'error', 7000); }
		finally { this._exporting = false; this.renderHeaderTools(); }
	}

	/** Present the generated purchase order in a modal: the file name, any notices, then a download button per file
	 *  format. The file contents already came back with the export, so the downloads are client-side blobs. */
	_showExportResult(pResult)
	{
		const tmpFiles = (pResult && Array.isArray(pResult.Files)) ? pResult.Files : [];
		if (!tmpFiles.length) { this._toast('The purchase order came back empty.', 'warning', 6000); return; }
		this._exportResult = pResult;   // stash for the download buttons
		const tmpWarnings = (pResult.Violations || []).map((pV) => `<li>${fEsc(pV.Message || pV.Type || 'Notice')}</li>`).join('');
		const tmpWarnHTML = tmpWarnings
			? `<div class="pf-export-warn"><i class="fas fa-triangle-exclamation"></i> This export has notices:<ul>${tmpWarnings}</ul></div>`
			: '';
		const tmpButtons = tmpFiles.map((pFile, pIndex) =>
		{
			const tmpKind = (Array.isArray(pFile.Types) && pFile.Types.length) ? pFile.Types.join(', ') : 'File';
			return `<button type="button" class="pf-tool-btn pf-tool-primary" onclick="_Pict.views.ProjectFunding.downloadExportFile(${pIndex})"><i class="fas fa-download"></i> Download ${fEsc(tmpKind)}</button>`;
		}).join('');
		const tmpContent = '<div class="pf-export-modal">'
			+ `<p class="pf-export-name"><i class="fas fa-file-invoice"></i> ${fEsc(pResult.FileName || 'Purchase order')}</p>`
			+ tmpWarnHTML
			+ `<div class="pf-export-actions">${tmpButtons}</div>`
			+ '</div>';
		if (this.modal && (typeof this.modal.show === 'function'))
		{
			this.modal.show({ title: 'Purchase order export', content: tmpContent, buttons: [ { Hash: 'close', Label: 'Close' } ], width: '560px' });
		}
		else { this.downloadExportFile(0); }   // no modal available — just download the primary file
	}

	downloadExportFile(pIndex)
	{
		const tmpResult = this._exportResult;
		const tmpFiles = (tmpResult && Array.isArray(tmpResult.Files)) ? tmpResult.Files : [];
		const tmpIndex = Number(pIndex) || 0;
		const tmpFile = tmpFiles[tmpIndex];
		if (!tmpFile) { return; }
		const tmpContent = String(tmpFile.Content == null ? '' : tmpFile.Content);
		this._triggerBrowserDownload(new Blob([ tmpContent ], { type: 'text/plain;charset=utf-8' }), this._exportFileName(tmpResult, tmpFile, tmpIndex));
	}

	/** The server sends one FileName (the primary/plaintext .dat); derive a distinct name for the other formats. */
	_exportFileName(pResult, pFile, pIndex)
	{
		const tmpBase = (pResult && pResult.FileName) ? String(pResult.FileName) : 'PurchaseOrder.dat';
		if (pIndex === 0) { return tmpBase; }
		const tmpKind = (pFile && Array.isArray(pFile.Types) && pFile.Types.length) ? pFile.Types.join('-') : `file-${pIndex}`;
		const tmpDot = tmpBase.lastIndexOf('.');
		return (tmpDot > 0) ? `${tmpBase.slice(0, tmpDot)}-${tmpKind}${tmpBase.slice(tmpDot)}` : `${tmpBase}-${tmpKind}`;
	}

	/** Client-side download of an in-memory blob (copied idiom from ImportantFiles view). */
	_triggerBrowserDownload(pBlob, pFileName)
	{
		const tmpURL = URL.createObjectURL(pBlob);
		const tmpAnchor = document.createElement('a');
		tmpAnchor.href = tmpURL;
		tmpAnchor.download = pFileName || 'download';
		tmpAnchor.rel = 'noopener';
		document.body.appendChild(tmpAnchor);
		tmpAnchor.click();
		document.body.removeChild(tmpAnchor);
		setTimeout(() => URL.revokeObjectURL(tmpURL), 10000);
	}

	// --- Funding simulation (what-if) -------------------------------------------------------------

	/** Open the simulation modal: a Configure tab (pick categorized line items + a payment amount each) and a Results
	 *  tab (how those payments would draw from the project's funds). Read-only — the scenario is never saved. */
	openSimulation()
	{
		if (!this.modal || (typeof this.modal.show !== 'function')) { this._toast('The simulation dialog needs the modal component.', 'error', 6000); return; }
		this._sim = { selected: [], result: null, error: null, tab: 'configure', searchTerm: '' };
		this.modal.show(
			{
				title: 'Funding simulation',
				content: '<div class="pf-sim">'
					+ '<div class="pf-sim-tabs">'
					+ '<button type="button" class="pf-sim-tab is-active" data-simtab="configure" onclick="_Pict.views.ProjectFunding.switchSimTab(\'configure\')">Configure</button>'
					+ '<button type="button" class="pf-sim-tab" data-simtab="results" onclick="_Pict.views.ProjectFunding.switchSimTab(\'results\')">Results</button>'
					+ '</div>'
					+ '<div id="PF-Sim-Configure" class="pf-sim-panel"></div>'
					+ '<div id="PF-Sim-Results" class="pf-sim-panel" hidden></div>'
					+ '</div>',
				buttons: [ { Hash: 'close', Label: 'Close' } ],
				width: '760px',
			});
		this.renderSimConfigure();
	}

	renderSimConfigure()
	{
		const tmpHost = document.getElementById('PF-Sim-Configure');
		if (!tmpHost) { return; }
		const tmpSelected = this._sim.selected;
		const tmpRows = tmpSelected.map((pSel) =>
			'<tr>'
			+ `<td class="pf-sim-liname">${fEsc(pSel.Name)}</td>`
			+ `<td class="pf-sim-licat"><span class="pf-tag pf-tag-type">${fEsc(pSel.CategoryName || '—')}</span></td>`
			+ `<td class="pf-sim-liamt"><span class="pf-sim-dollar">$</span><input class="pf-sim-amt" type="number" min="0" step="any" inputmode="decimal" value="${fAttr(pSel.Amount)}" data-simkey="${fAttr(pSel.Key)}" oninput="_Pict.views.ProjectFunding.onSimAmountEdit(this)" placeholder="0.00" /></td>`
			+ `<td class="pf-sim-lidel"><button type="button" class="pf-btn pf-btn-danger" title="Remove" onclick="_Pict.views.ProjectFunding.simRemoveLineItem('${fAttr(pSel.Key)}')"><i class="fas fa-xmark"></i></button></td>`
			+ '</tr>').join('');
		const tmpSelectedHTML = tmpSelected.length
			? `<table class="pf-sim-table"><thead><tr><th>Line item</th><th>Category</th><th>Payment amount</th><th></th></tr></thead><tbody>${tmpRows}</tbody></table>`
			: '<div class="pf-note">No line items selected yet — search above and click to add.</div>';
		const tmpReady = this._simReady();
		tmpHost.innerHTML =
			'<p class="pf-sim-help">Pick categorized line items and enter a payment amount for each. The simulation shows how those payments would draw from the project’s funds — nothing is saved.</p>'
			+ `<input id="PF-Sim-Search" class="input pf-sim-search" type="text" autocomplete="off" placeholder="Search line items by name or code…" value="${fAttr(this._sim.searchTerm || '')}" oninput="_Pict.views.ProjectFunding.onSimSearch(this.value)" />`
			+ '<div id="PF-Sim-PickList" class="pf-sim-picklist"></div>'
			+ `<div class="pf-sim-selected">${tmpSelectedHTML}</div>`
			+ '<div class="pf-sim-foot">'
			+ '<button type="button" class="pf-tool-btn" onclick="_Pict.views.ProjectFunding.simReset()"><i class="fas fa-rotate-left"></i> Reset</button>'
			+ `<button type="button" class="pf-tool-btn pf-tool-primary pf-sim-run"${tmpReady ? '' : ' disabled'} onclick="_Pict.views.ProjectFunding.runSimulation()"><i class="fas fa-play"></i> Run simulation</button>`
			+ '</div>';
		this._renderSimPickList(this._sim.searchTerm || '');
	}

	onSimSearch(pTerm)
	{
		this._sim.searchTerm = pTerm;
		if (this._simTimer) { clearTimeout(this._simTimer); }
		this._simTimer = setTimeout(() => this._renderSimPickList(pTerm), 150);
	}

	/** The categorized, not-yet-selected line items matching the search term (client-side over the loaded set). */
	_renderSimPickList(pTerm)
	{
		const tmpHost = document.getElementById('PF-Sim-PickList');
		if (!tmpHost) { return; }
		const tmpTerm = String(pTerm || '').trim().toLowerCase();
		const tmpSelectedKeys = new Set(this._sim.selected.map((pS) => String(pS.Key)));
		const tmpPool = this.provider.lineItemRows().filter((pR) => !pR.IsUncategorized && !tmpSelectedKeys.has(String(pR.Key)));
		const tmpMatches = [];
		for (let i = 0; (i < tmpPool.length) && (tmpMatches.length < 60); i++)
		{
			const tmpR = tmpPool[i];
			if (!tmpTerm || (String(tmpR.Name).toLowerCase().indexOf(tmpTerm) >= 0) || (String(tmpR.ItemCode).toLowerCase().indexOf(tmpTerm) >= 0)) { tmpMatches.push(tmpR); }
		}
		this._simMatches = tmpMatches;
		if (!tmpMatches.length) { tmpHost.innerHTML = '<div class="pf-note">No categorized line items match.</div>'; return; }
		tmpHost.innerHTML = tmpMatches.map((pR, pIndex) =>
			`<button type="button" class="pf-sim-pickrow" onclick="_Pict.views.ProjectFunding.simAddLineItem(${pIndex})"><span class="pf-sim-pickname">${fEsc(pR.Name)}</span><span class="pf-tag pf-tag-type">${fEsc(pR.CategoryName)}</span><i class="fas fa-plus"></i></button>`).join('');
	}

	simAddLineItem(pIndex)
	{
		const tmpR = (this._simMatches || [])[pIndex];
		if (!tmpR) { return; }
		if (this._sim.selected.some((pS) => String(pS.Key) === String(tmpR.Key))) { return; }
		this._sim.selected.push({ Key: tmpR.Key, Name: tmpR.Name, CategoryKey: tmpR.CategoryKey, CategoryName: tmpR.CategoryName, Amount: '' });
		this.renderSimConfigure();
	}

	simRemoveLineItem(pKey)
	{
		this._sim.selected = this._sim.selected.filter((pS) => String(pS.Key) !== String(pKey));
		this.renderSimConfigure();
	}

	/** Amount edits update state + toggle the Run button in place — never a re-render, so the input keeps focus. */
	onSimAmountEdit(pEl)
	{
		if (!pEl) { return; }
		const tmpSel = this._sim.selected.find((pS) => String(pS.Key) === String(pEl.getAttribute('data-simkey')));
		if (tmpSel) { tmpSel.Amount = pEl.value; }
		const tmpRun = document.querySelector('.pf-sim-run');
		if (tmpRun) { tmpRun.disabled = !this._simReady(); }
	}

	_simReady()
	{
		const tmpSel = (this._sim && this._sim.selected) || [];
		if (!tmpSel.length) { return false; }
		return tmpSel.every((pS) => { const tmpN = Number(pS.Amount); return (pS.Amount !== '') && Number.isFinite(tmpN) && (tmpN > 0); });
	}

	simReset() { this._sim.selected = []; this._sim.searchTerm = ''; this.renderSimConfigure(); }

	async runSimulation()
	{
		if (!this._simReady() || this._simRunning) { return; }
		this._simRunning = true;
		const tmpRun = document.querySelector('.pf-sim-run');
		if (tmpRun) { tmpRun.disabled = true; tmpRun.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Running…'; }
		const tmpPayload = this._sim.selected.map((pS) => ({ LineItem: { Key: pS.Key }, Category: { Key: pS.CategoryKey }, Amount: String(pS.Amount) }));
		try
		{
			this._sim.result = await this.provider.simulateAsync(tmpPayload);
			this._sim.error = null;
		}
		catch (pError)
		{
			this._sim.error = (pError && pError.message) ? pError.message : 'The simulation failed.';
			this._sim.result = null;
		}
		finally
		{
			this._simRunning = false;
			this.renderSimResults();
			this.switchSimTab('results');
			const tmpRun2 = document.querySelector('.pf-sim-run');
			if (tmpRun2) { tmpRun2.disabled = !this._simReady(); tmpRun2.innerHTML = '<i class="fas fa-play"></i> Run simulation'; }
		}
	}

	switchSimTab(pTab)
	{
		if (this._sim) { this._sim.tab = pTab; }
		const tmpCfg = document.getElementById('PF-Sim-Configure');
		const tmpRes = document.getElementById('PF-Sim-Results');
		if (tmpCfg) { tmpCfg.hidden = (pTab !== 'configure'); }
		if (tmpRes) { tmpRes.hidden = (pTab !== 'results'); }
		const tmpTabs = document.querySelectorAll('.pf-sim-tab');
		for (let i = 0; i < tmpTabs.length; i++) { tmpTabs[i].classList.toggle('is-active', tmpTabs[i].getAttribute('data-simtab') === pTab); }
	}

	renderSimResults()
	{
		const tmpHost = document.getElementById('PF-Sim-Results');
		if (!tmpHost) { return; }
		if (this._sim.error)
		{
			tmpHost.innerHTML = `<div class="pf-sim-err"><i class="fas fa-triangle-exclamation"></i> ${fEsc(this._sim.error)}</div>`
				+ '<div class="pf-sim-foot"><button type="button" class="pf-tool-btn" onclick="_Pict.views.ProjectFunding.switchSimTab(\'configure\')"><i class="fas fa-arrow-left"></i> Back to configure</button></div>';
			return;
		}
		const tmpSol = (this._sim.result && this._sim.result.Solution) || {};
		const tmpScn = (this._sim.result && this._sim.result.Scenario) || {};
		const tmpTypes = Array.isArray(tmpSol.FundingTypeOutcomes) ? tmpSol.FundingTypeOutcomes : [];
		const tmpFunds = Array.isArray(tmpSol.FundOutcomes) ? tmpSol.FundOutcomes : [];
		const tmpViolations = Array.isArray(tmpSol.Violations) ? tmpSol.Violations : [];
		const tmpLog = Array.isArray(tmpScn.LogEntry) ? tmpScn.LogEntry : [];

		const tmpStats = '<div class="pf-sim-stats">'
			+ `<div class="pf-sim-stat"><span class="pf-sim-stat-lbl">Requested payment</span><span class="pf-sim-stat-val">${fMoney(tmpSol.FinalPaymentAmount)}</span></div>`
			+ `<div class="pf-sim-stat"><span class="pf-sim-stat-lbl">Funded</span><span class="pf-sim-stat-val">${fMoney(tmpSol.ActualPaymentAmount)}</span></div>`
			+ '</div>';
		const tmpViolHTML = tmpViolations.length
			? `<div class="pf-violations">${tmpViolations.map((pV) => `<div class="pf-violation"><i class="fas fa-triangle-exclamation"></i> ${fEsc(pV.Message || pV.Type || 'Funding issue')}</div>`).join('')}</div>`
			: '<div class="pf-ok"><i class="fas fa-circle-check"></i> No simulation violations.</div>';
		const tmpTypeHTML = tmpTypes.length
			? `<div class="pf-outcomes"><div class="pf-outcomes-head">By funding type</div>${tmpTypes.map((pO) => `<div class="pf-outcome"><span>${fEsc(pO.FundingType)}</span><span>${fMoney(pO.Amount)}</span></div>`).join('')}</div>`
			: '';
		const tmpFundHTML = tmpFunds.length
			? `<div class="pf-outcomes"><div class="pf-outcomes-head">${tmpFunds.length} fund${tmpFunds.length === 1 ? '' : 's'} drawn</div>${tmpFunds.map((pF) => `<div class="pf-outcome"><span>${fEsc(pF.Name || pF.Hash || 'Fund')}</span><span>${fMoney(pF.Amount)}</span></div>`).join('')}</div>`
			: '<div class="pf-note">No funds were drawn for these payments.</div>';
		const tmpLogHTML = tmpLog.length
			? `<div class="pf-sim-log"><div class="pf-outcomes-head">Depletion history</div>${tmpLog.map((pL) => `<div class="pf-sim-logrow">${fEsc(pL.Message || pL.Description || '')}</div>`).join('')}</div>`
			: '';
		tmpHost.innerHTML = tmpStats
			+ tmpViolHTML
			+ `<div class="pf-sim-outcome-row">${tmpTypeHTML}${tmpFundHTML}</div>`
			+ tmpLogHTML
			+ '<div class="pf-sim-foot"><button type="button" class="pf-tool-btn" onclick="_Pict.views.ProjectFunding.switchSimTab(\'configure\')"><i class="fas fa-arrow-left"></i> Adjust and re-run</button></div>';
	}

	// --- Accounting overview ----------------------------------------------------------------------

	renderAccounting()
	{
		const tmpMount = this.pict.ContentAssignment.getElement(ACCT_MOUNT);
		if (!tmpMount || (tmpMount.length === 0)) { return; }
		const tmpState = this.pict.AppData.ProjectFunding || {};
		const tmpDigest = tmpState.Digest || {};
		const tmpSolution = tmpState.Solution || {};
		const tmpTotals = Array.isArray(tmpDigest.TotalsSummary) ? tmpDigest.TotalsSummary : [];
		const tmpViolations = Array.isArray(tmpSolution.Violations) ? tmpSolution.Violations : [];

		const tmpViolationHTML = tmpViolations.length
			? `<div class="pf-violations">${tmpViolations.map((pV) => `<div class="pf-violation"><i class="fas fa-triangle-exclamation"></i> ${fEsc(pV.Message || pV.Type || 'Funding issue')}</div>`).join('')}</div>`
			: '<div class="pf-ok"><i class="fas fa-circle-check"></i> No funding violations.</div>';

		const tmpTotalCards = tmpTotals.map((pT) =>
		{
			const tmpPctDepleted = Number(pT.PercentageDepleted) || 0;
			const tmpAmber = tmpPctDepleted > 1;
			return `<div class="pf-total-card"><div class="pf-total-name">${fEsc(pT.Name || pT.Key || 'Funds')}</div>`
				+ `<div class="pf-total-grid">`
				+ `<div><span class="pf-total-lbl">Original</span><span class="pf-total-val">${fMoney(pT.OriginalBalance)}</span></div>`
				+ `<div><span class="pf-total-lbl">Balance</span><span class="pf-total-val">${fMoney(pT.Balance)}</span></div>`
				+ `<div><span class="pf-total-lbl">Committed</span><span class="pf-total-val">${fMoney(pT.TotalCommitted)}</span></div>`
				+ `<div><span class="pf-total-lbl">Spent</span><span class="pf-total-val">${fMoney(pT.Expenditure)}</span></div>`
				+ `</div>`
				+ `<div class="pf-bar"><div class="pf-bar-fill${tmpAmber ? ' is-over' : ''}" style="width:${Math.max(0, Math.min(100, tmpPctDepleted * 100)).toFixed(1)}%"></div></div>`
				+ `<div class="pf-bar-lbl">${fPct(pT.PercentageDepleted)} depleted · ${fPct(pT.PercentageRemaining)} remaining</div></div>`;
		}).join('');

		const tmpTypeOutcomes = Array.isArray(tmpSolution.FundingTypeOutcomes) ? tmpSolution.FundingTypeOutcomes : [];
		const tmpTypeHTML = tmpTypeOutcomes.length
			? `<div class="pf-outcomes"><div class="pf-outcomes-head">Funding type</div>${tmpTypeOutcomes.map((pO) => `<div class="pf-outcome"><span>${fEsc(pO.FundingType)}</span><span>${fMoney(pO.Amount)}</span></div>`).join('')}</div>`
			: '';
		const tmpAcctOutcomes = Array.isArray(tmpSolution.AccountTypeOutcomes) ? tmpSolution.AccountTypeOutcomes : [];
		const tmpAcctHTML = tmpAcctOutcomes.length
			? `<div class="pf-outcomes"><div class="pf-outcomes-head">Account type</div>${tmpAcctOutcomes.map((pO) => `<div class="pf-outcome"><span>${fEsc(pO.AccountType)}</span><span>${fMoney(pO.Amount)}</span></div>`).join('')}</div>`
			: '';

		this.pict.ContentAssignment.assignContent(ACCT_MOUNT,
			tmpViolationHTML
			+ (tmpTotalCards ? `<div class="pf-total-row">${tmpTotalCards}</div>` : '<div class="pf-note">No fund totals yet.</div>')
			+ `<div class="pf-outcome-row">${tmpTypeHTML}${tmpAcctHTML}</div>`);
	}

	// --- Line item funding (slice 2) --------------------------------------------------------------

	renderLineItems()
	{
		const tmpMount = this.pict.ContentAssignment.getElement('#ProjectFunding-LineItems');
		if (!tmpMount || (tmpMount.length === 0)) { return; }
		// When the project's status requires a Change Order, line-item modifications (funding-category assignment
		// writes LineItem.IDFundingCategory) are locked — force the read-only render and show a prompt.
		const tmpRequiresCO = !!(this.pict.AppData.ProjectFunding && this.pict.AppData.ProjectFunding.RequiresChangeOrder);
		const tmpManage = !!(this.pict.AppData.ProjectFunding && this.pict.AppData.ProjectFunding.CanManage) && !tmpRequiresCO;
		const tmpCOBanner = tmpRequiresCO
			? `<div class="pf-co-lock"><i class="fas fa-lock"></i> This project’s status <strong>${fEsc((this.pict.AppData.ProjectFunding && this.pict.AppData.ProjectFunding.ProjectStatus) || '')}</strong> requires a <strong>Change Order</strong> to modify line items — funding assignments are read-only. Use the <strong>Change Orders</strong> tab.</div>`
			: '';
		const tmpRows = this.provider.lineItemRows();
		const tmpCats = this.provider.projectCategories || [];
		const tmpCountEl = document.getElementById('ProjectFunding-LICount');
		if (!tmpRows.length)
		{
			if (tmpCountEl) { tmpCountEl.textContent = ''; }
			this.pict.ContentAssignment.assignContent('#ProjectFunding-LineItems', '<div class="pf-note">This project has no line items.</div>');
			return;
		}
		const tmpUncat = tmpRows.filter((pR) => pR.IsUncategorized).length;
		if (tmpCountEl) { tmpCountEl.textContent = tmpUncat ? `${tmpUncat} of ${tmpRows.length} uncategorized` : `${tmpRows.length} line items`; }
		if (!tmpCats.length && tmpManage)
		{
			this.pict.ContentAssignment.assignContent('#ProjectFunding-LineItems', '<div class="pf-note">Add a funding category in the setup panel before assigning line items.</div>');
			return;
		}
		// The common case is a searchable Pict-Section-Picker per row (fed the project's funding categories as a
		// static, in-memory Options list). A project with a huge line-item set falls back to native <select>s so we
		// don't stand up hundreds of picker views at once. The row keys are Finance display keys (arbitrary strings),
		// so rows are addressed by index — RowKeys[i] maps the mount/AppData slot back to the line item's Key.
		const tmpUsePickers = tmpManage && (tmpRows.length <= LI_PICKER_CAP);
		this._liRowKeys = tmpRows.map((pR) => pR.Key);
		this._liUsePickers = tmpUsePickers;
		const tmpBigNote = (tmpManage && !tmpUsePickers)
			? '<div class="pf-note pf-li-note">This project has a lot of line items — using quick dropdowns instead of the searchable picker to stay responsive.</div>'
			: '';
		// A line item can't be un-assigned once categorized (the Finance API only reassigns) — the native placeholder
		// is a DISABLED option, and the picker simply shows its Placeholder until a real category is chosen.
		const tmpNativeOptions = (pSelected) => [ `<option value="" disabled${pSelected ? '' : ' selected'}>Select a category…</option>` ]
			.concat(tmpCats.map((pCat) => `<option value="${fAttr(pCat.Key)}"${String(pCat.Key) === String(pSelected) ? ' selected' : ''}>${fEsc(pCat.Name)}</option>`)).join('');
		const tmpBody = tmpRows.map((pR, pIndex) =>
		{
			let tmpCatCell;
			if (!tmpManage) { tmpCatCell = `<span class="pf-ro">${pR.CategoryName ? fEsc(pR.CategoryName) : '—'}</span>`; }
			else if (tmpUsePickers) { tmpCatCell = `<div class="pf-li-pickmount" id="ProjectFunding-LICat-${pIndex}"></div>`; }
			else { tmpCatCell = `<select class="pf-li-cat" data-li-index="${pIndex}" onchange="_Pict.views.ProjectFunding.onLineItemCategoryChange(this)">${tmpNativeOptions(pR.CategoryKey)}</select>`; }
			return `<tr class="pf-li-row${pR.IsUncategorized ? ' is-uncat' : ''}"><td class="pf-li-code">${fEsc(pR.ItemCode)}</td><td class="pf-li-name">${fEsc(pR.Name)}${pR.IsUncategorized ? '<span class="pf-li-badge">no category</span>' : ''}</td><td class="pf-li-catcell">${tmpCatCell}</td></tr>`;
		}).join('');
		this.pict.ContentAssignment.assignContent('#ProjectFunding-LineItems',
			`${tmpCOBanner}${tmpBigNote}<table class="pf-li-table"><thead><tr><th>Item</th><th>Name</th><th>Funding category</th></tr></thead><tbody>${tmpBody}</tbody></table>`);

		if (tmpUsePickers) { this._mountLineItemPickers(tmpRows, tmpCats); }
	}

	/** Stand up one searchable Pict-Section-Picker per line-item row, each fed the project's funding categories as a
	 *  static in-memory Options list. Selecting a category calls straight through to the same save path as the native
	 *  fallback. Uncategorized rows are left showing the placeholder (there is no un-assign). */
	_mountLineItemPickers(pRows, pCats)
	{
		const tmpPicker = this.pickerProvider;
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function') || !tmpCA) { return; }
		this.pict.AppData.ProjectFunding = this.pict.AppData.ProjectFunding || {};
		const tmpStore = (this.pict.AppData.ProjectFunding.LICatValues = {});
		const tmpOptions = pCats.map((pCat) => ({ Value: String(pCat.Key), Text: pCat.Name }));

		for (let i = 0; i < pRows.length; i++)
		{
			const tmpDest = `#ProjectFunding-LICat-${i}`;
			if (tmpCA.getElement(tmpDest).length === 0) { continue; }
			const tmpValue = pRows[i].IsUncategorized ? '' : String(pRows[i].CategoryKey);
			tmpStore[i] = tmpValue;

			const tmpInstance = tmpPicker.createPicker(`ProjectFunding-LICat-${i}`,
				{
					DestinationAddress: tmpDest,
					ValueAddress: `AppData.ProjectFunding.LICatValues.${i}`,
					Searchable: true,
					Placeholder: 'Select a category…',
					Options: tmpOptions,
					OnChange: ((pRowIndex) => (pNewValue) => this.onLineItemPick(pRowIndex, pNewValue))(i),
				});
			if (tmpInstance && (typeof tmpInstance.render === 'function')) { tmpInstance.render(); }
			if (tmpInstance && tmpValue && (typeof tmpInstance.setValue === 'function')) { tmpInstance.setValue(tmpValue); }
		}
	}

	/** Picker OnChange — resolve the row index back to its line-item Key and assign the chosen category. */
	onLineItemPick(pRowIndex, pValue) { this._applyLineItemCategory((this._liRowKeys || [])[pRowIndex], pValue); }

	/** Native-fallback onchange (large projects). */
	onLineItemCategoryChange(pEl)
	{
		if (!pEl) { return; }
		const tmpIndex = parseInt(pEl.getAttribute('data-li-index'), 10);
		pEl.disabled = true;
		this._applyLineItemCategory((this._liRowKeys || [])[tmpIndex], pEl.value)
			.finally(() => { if (pEl && pEl.parentNode) { pEl.disabled = false; } });
	}

	/** Assign a line item's funding category. A full re-render follows (the provider re-solves) — fine here: it's a
	 *  discrete pick (no mid-type edit) and a category change ripples through the whole solution/accounting. */
	async _applyLineItemCategory(pRowKey, pCatValue)
	{
		if (!pRowKey || !pCatValue) { return; }
		try { await this.provider.saveLineItemCategoryAsync(pRowKey, pCatValue); this._toast('Line item funding updated.', 'success'); }
		catch (pError) { this._toast((pError && pError.message) ? pError.message : 'Could not update the line item.', 'error', 6000); }
	}
}

module.exports = ProjectFunding;
module.exports.default_configuration = {
	ViewIdentifier: 'ProjectFunding',
	DefaultRenderable: 'ProjectFundingWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,

	CSS: /*css*/`
		.pf-wrap { max-width: 1180px; margin: 0 auto; padding: 1.5rem 1.25rem 4rem; }
		/* Embedded in a ProjectWorkspace Contract ▸ Funding tab: shed the standalone-page chrome + the redundant
		   project title (the workspace header + the "Contract ▸ Funding" breadcrumb already name the project), leaving
		   the header as a right-aligned Funding Sim / PO Export toolbar. */
		.section.pf-embedded { padding: 0; }
		.pf-embedded .pf-wrap { max-width: none; padding: 0.25rem 0 1rem; }
		.pf-embedded .pf-head { margin-bottom: 0.6rem; }
		.pf-embedded .pf-head-ic, .pf-embedded .pf-title { display: none; }
		.pf-head { display: flex; align-items: flex-start; gap: 0.85rem; margin-bottom: 1.1rem; }
		.pf-head-ic { flex: none; width: 2.4rem; height: 2.4rem; border-radius: 10px; display: grid; place-items: center; background: var(--theme-color-background-selected, #e3edfb); color: var(--theme-color-brand-primary, #156dd1); font-size: 1.1rem; }
		.pf-title { font-size: 1.5rem; font-weight: 700; margin: 0; line-height: 1.15; color: var(--theme-color-text-primary, #1f2733); }
		.pf-head-spacer { flex: 1 1 auto; }
		.pf-head-btn, .pf-tool-btn { display: inline-flex; align-items: center; gap: 0.4rem; white-space: nowrap; font-size: 0.82rem; cursor: pointer; padding: 0.4rem 0.75rem; border-radius: 7px; border: 1px solid var(--theme-color-border-default, #d7dce3); background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-secondary, #45505f); }
		.pf-head-btn:hover, .pf-tool-btn:hover { background: var(--theme-color-background-secondary, #f0f2f6); }
		.pf-tool-primary { border-color: var(--theme-color-brand-primary, #156dd1); color: var(--theme-color-brand-primary, #156dd1); }
		.pf-ro-pill { align-self: center; display: inline-flex; align-items: center; gap: 0.35rem; font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; padding: 0.25rem 0.55rem; border-radius: 999px; background: var(--theme-color-background-secondary, #f0f2f6); color: var(--theme-color-text-muted, #8a94a3); border: 1px solid var(--theme-color-border-light, #e8ebf0); }

		.pf-state { padding: 2.5rem 1rem; text-align: center; color: var(--theme-color-text-secondary, #5b6673); }
		.pf-note { padding: 0.9rem; color: var(--theme-color-text-muted, #8a94a3); font-size: 0.86rem; text-align: center; }
		.pf-err { color: var(--theme-color-status-error, #cf5b5b); }

		/* Front door */
		.pf-picker { max-width: 620px; margin: 0.5rem auto 0; }
		.pf-proj-filter { width: 100%; padding: 0.6rem 0.8rem; border: 1px solid var(--theme-color-border-default, #d7dce3); border-radius: 8px; font: inherit; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1f2733); margin-bottom: 0.8rem; }
		.pf-proj-list { border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 10px; overflow: hidden; }
		.pf-proj-row { display: flex; align-items: center; justify-content: space-between; gap: 0.6rem; width: 100%; text-align: left; border: none; border-bottom: 1px solid var(--theme-color-border-light, #e8ebf0); background: var(--theme-color-background-panel, #fff); padding: 0.65rem 0.9rem; cursor: pointer; font: inherit; color: var(--theme-color-text-primary, #1f2733); }
		.pf-proj-row:last-child { border-bottom: none; }
		.pf-proj-row:hover { background: var(--theme-color-background-secondary, #f0f2f6); }
		.pf-proj-row i { color: var(--theme-color-text-muted, #8a94a3); font-size: 0.8rem; }

		/* Layout: two columns (setup grid + accounting) on wide screens */
		/* Full-width stack: accounting overview (collapsible) on top, then the full-width funding setup, then line items. */
		.pf-stack { display: flex; flex-direction: column; gap: 1.25rem; }
		.pf-panel { border: 1px solid var(--theme-color-border-default, #d7dce3); border-radius: 12px; background: var(--theme-color-background-panel, #fff); }
		.pf-acct-head { cursor: pointer; user-select: none; }
		.pf-acct-head:hover { background: var(--theme-color-background-secondary, #f0f2f6); }
		.pf-acct-caret { color: var(--theme-color-text-muted, #8a94a3); font-size: 0.85em; }
		.pf-acct-panel.is-collapsed .pf-panel-head { border-bottom: none; }
		.pf-acct-panel.is-collapsed .pf-acct-body { display: none; }
		.pf-panel-head { display: flex; align-items: center; gap: 0.6rem; padding: 0.7rem 0.9rem; border-bottom: 1px solid var(--theme-color-border-light, #e8ebf0); font-weight: 600; font-size: 0.92rem; color: var(--theme-color-text-primary, #1f2733); }
		.pf-panel-head .pf-head-spacer { flex: 1 1 auto; }
		.pf-panel-body { padding: 0.6rem 0.7rem; }

		/* Setup grid */
		.pf-empty { text-align: center; padding: 2.2rem 1rem; color: var(--theme-color-text-muted, #8a94a3); }
		.pf-empty i { font-size: 1.6rem; display: block; margin-bottom: 0.5rem; opacity: 0.7; }
		.pf-empty p { margin: 0 0 0.8rem; }
		.pf-cat { border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 9px; margin-bottom: 0.7rem; overflow: hidden; }
		.pf-cat-head { display: flex; align-items: center; gap: 0.5rem; padding: 0.5rem 0.7rem; background: var(--theme-color-background-secondary, #f0f2f6); }
		.pf-cat-head > i { color: var(--theme-color-brand-primary, #156dd1); }
		.pf-cat-name { font-weight: 600; font-size: 0.9rem; }
		.pf-tag { font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.03em; color: var(--theme-color-text-muted, #8a94a3); background: var(--theme-color-background-panel, #fff); border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 999px; padding: 0.05rem 0.4rem; }
		.pf-tag-type { color: var(--theme-color-brand-primary, #156dd1); }
		.pf-cat-actions { margin-left: auto; display: inline-flex; gap: 0.3rem; }
		.pf-cat-empty { padding: 0.5rem 0.8rem; color: var(--theme-color-text-muted, #8a94a3); font-size: 0.82rem; font-style: italic; }
		.pf-btn { display: inline-flex; align-items: center; gap: 0.3rem; font-size: 0.74rem; cursor: pointer; padding: 0.25rem 0.5rem; border-radius: 6px; border: 1px solid var(--theme-color-border-default, #d7dce3); background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-secondary, #45505f); }
		.pf-btn:hover { background: var(--theme-color-background-selected, #e3edfb); color: var(--theme-color-brand-primary, #156dd1); }
		.pf-btn-danger:hover { color: var(--theme-color-status-error, #cf5b5b); background: rgba(207,91,91,0.1); }
		.pf-btn[disabled] { opacity: 0.55; cursor: default; }

		/* Header tools (purchase-order export) */
		.pf-head-tools { display: inline-flex; align-items: center; gap: 0.5rem; }
		.pf-export-btn[disabled] { opacity: 0.6; cursor: default; }

		/* Purchase-order export modal */
		.pf-export-modal { font-size: 0.9rem; }
		.pf-export-name { display: flex; align-items: center; gap: 0.45rem; font-weight: 600; color: var(--theme-color-text-primary, #1f2733); margin: 0 0 0.8rem; word-break: break-all; }
		.pf-export-warn { font-size: 0.83rem; color: #8a6d1b; background: var(--theme-color-status-warning-bg, #fbf3d9); border-radius: 7px; padding: 0.55rem 0.7rem; margin: 0 0 0.9rem; }
		.pf-export-warn ul { margin: 0.35rem 0 0; padding-left: 1.1rem; }
		.pf-export-actions { display: flex; flex-wrap: wrap; gap: 0.55rem; }

		/* Funding simulation modal */
		.pf-sim { font-size: 0.9rem; }
		.pf-sim-tabs { display: flex; gap: 0.4rem; border-bottom: 1px solid var(--theme-color-border-light, #e8ebf0); margin: 0 0 0.9rem; }
		.pf-sim-tab { appearance: none; border: none; background: none; cursor: pointer; font: inherit; font-size: 0.85rem; font-weight: 600; color: var(--theme-color-text-muted, #8a94a3); padding: 0.45rem 0.75rem; border-bottom: 2px solid transparent; margin-bottom: -1px; }
		.pf-sim-tab.is-active { color: var(--theme-color-brand-primary, #156dd1); border-bottom-color: var(--theme-color-brand-primary, #156dd1); }
		.pf-sim-help { color: var(--theme-color-text-secondary, #5b6673); font-size: 0.84rem; margin: 0 0 0.7rem; }
		.pf-sim-search { width: 100%; padding: 0.5rem 0.7rem; border: 1px solid var(--theme-color-border-default, #d7dce3); border-radius: 7px; font: inherit; margin: 0 0 0.5rem; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1f2733); }
		.pf-sim-picklist { max-height: 180px; overflow-y: auto; border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 7px; margin: 0 0 0.9rem; }
		.pf-sim-picklist:empty { display: none; }
		.pf-sim-pickrow { display: flex; align-items: center; gap: 0.6rem; width: 100%; text-align: left; appearance: none; border: none; border-bottom: 1px solid var(--theme-color-border-light, #eef1f5); background: var(--theme-color-background-panel, #fff); cursor: pointer; font: inherit; font-size: 0.84rem; padding: 0.45rem 0.65rem; color: var(--theme-color-text-primary, #1f2733); }
		.pf-sim-pickrow:last-child { border-bottom: none; }
		.pf-sim-pickrow:hover { background: var(--theme-color-background-selected, #e3edfb); }
		.pf-sim-pickname { flex: 1 1 auto; }
		.pf-sim-pickrow .fa-plus { color: var(--theme-color-brand-primary, #156dd1); }
		.pf-sim-table { width: 100%; border-collapse: collapse; font-size: 0.84rem; }
		.pf-sim-table th { text-align: left; font-size: 0.64rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-muted, #8a94a3); padding: 0.35rem 0.5rem; border-bottom: 1px solid var(--theme-color-border-light, #e8ebf0); }
		.pf-sim-table td { padding: 0.35rem 0.5rem; border-bottom: 1px solid var(--theme-color-border-light, #eef1f5); vertical-align: middle; }
		.pf-sim-liname { font-weight: 600; color: var(--theme-color-text-primary, #1f2733); }
		.pf-sim-liamt { white-space: nowrap; }
		.pf-sim-dollar { color: var(--theme-color-text-muted, #8a94a3); margin-right: 0.15rem; }
		.pf-sim-amt { width: 8.5rem; padding: 0.25rem 0.4rem; border: 1px solid var(--theme-color-border-default, #d7dce3); border-radius: 6px; font: inherit; font-size: 0.82rem; text-align: right; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1f2733); }
		.pf-sim-foot { display: flex; justify-content: flex-end; gap: 0.55rem; margin-top: 1rem; }
		.pf-sim-stats { display: flex; gap: 0.85rem; flex-wrap: wrap; margin: 0 0 0.9rem; }
		.pf-sim-stat { flex: 1 1 180px; border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 9px; padding: 0.7rem 0.85rem; background: var(--theme-color-background-secondary, #fafbfc); }
		.pf-sim-stat-lbl { display: block; font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-muted, #8a94a3); margin-bottom: 0.25rem; }
		.pf-sim-stat-val { font-size: 1.2rem; font-weight: 700; color: var(--theme-color-text-primary, #1f2733); font-variant-numeric: tabular-nums; }
		.pf-sim-outcome-row { display: flex; gap: 0.85rem; flex-wrap: wrap; margin: 0.6rem 0; }
		.pf-sim-outcome-row > * { flex: 1 1 220px; }
		.pf-sim-log { margin-top: 0.6rem; }
		.pf-sim-logrow { font-size: 0.8rem; color: var(--theme-color-text-secondary, #5b6673); padding: 0.3rem 0.5rem; border-bottom: 1px solid var(--theme-color-border-light, #eef1f5); font-variant-numeric: tabular-nums; }
		.pf-sim-err { display: flex; align-items: center; gap: 0.5rem; font-size: 0.88rem; color: var(--theme-color-status-error, #a12d22); background: var(--theme-color-status-danger-bg, #fbe6e3); border-radius: 8px; padding: 0.7rem 0.85rem; }
		.pf-src-tablewrap { overflow-x: auto; }
		.pf-src-table { width: 100%; border-collapse: collapse; font-size: 0.82rem; }
		/* Reserve room for the (long) funding-source names so they stop hard-wrapping now that the setup is full width;
		   the numeric inputs stay compact (min-width) and the table scrolls horizontally if the viewport is narrow. */
		.pf-src-table th:first-child, .pf-src-name, .pf-fund-name { min-width: 15rem; }
		.pf-src-table th { text-align: left; font-size: 0.64rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-muted, #8a94a3); padding: 0.35rem 0.5rem; border-bottom: 1px solid var(--theme-color-border-light, #e8ebf0); }
		.pf-src-table td { padding: 0.3rem 0.5rem; border-bottom: 1px solid var(--theme-color-border-light, #eef1f5); vertical-align: middle; }
		.pf-src-row .pf-src-name { font-weight: 600; color: var(--theme-color-text-primary, #1f2733); }
		.pf-fund-row td { background: var(--theme-color-background-secondary, #fafbfc); }
		.pf-fund-name { color: var(--theme-color-text-secondary, #5b6673); padding-left: 1rem; }
		.pf-fund-name i { color: var(--theme-color-text-muted, #8a94a3); font-size: 0.7rem; margin-right: 0.2rem; }
		.pf-fund-bal { font-size: 0.72rem; color: var(--theme-color-text-muted, #8a94a3); font-variant-numeric: tabular-nums; }
		.pf-eff { font-variant-numeric: tabular-nums; color: var(--theme-color-text-muted, #8a94a3); }
		.pf-row-act { white-space: nowrap; text-align: right; }
		.pf-row-act .pf-btn { margin-left: 0.25rem; }
		.pf-inp { width: 100%; min-width: 52px; max-width: 7rem; padding: 0.2rem 0.35rem; border: 1px solid var(--theme-color-border-default, #d7dce3); border-radius: 5px; font: inherit; font-size: 0.8rem; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1f2733); font-variant-numeric: tabular-nums; }
		.pf-inp:focus { outline: 2px solid var(--theme-color-brand-primary, #156dd1); outline-offset: -1px; }
		.pf-inp-text { min-width: 70px; }
		.pf-ro { font-variant-numeric: tabular-nums; color: var(--theme-color-text-secondary, #5b6673); }

		/* Accounting overview */
		.pf-violations { display: flex; flex-direction: column; gap: 0.4rem; margin-bottom: 0.7rem; }
		.pf-violation { font-size: 0.8rem; padding: 0.45rem 0.6rem; border-radius: 7px; background: rgba(207,91,91,0.1); color: var(--theme-color-status-error, #cf5b5b); }
		.pf-ok { font-size: 0.8rem; padding: 0.45rem 0.6rem; border-radius: 7px; background: rgba(72,163,106,0.12); color: var(--theme-color-status-success, #3c9d63); margin-bottom: 0.7rem; }
		.pf-total-row { display: flex; flex-flow: row wrap; gap: 0.6rem; }
		.pf-total-card { flex: 1 1 240px; border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 9px; padding: 0.6rem 0.7rem; }
		.pf-total-name { font-size: 0.8rem; font-weight: 600; margin-bottom: 0.4rem; color: var(--theme-color-text-primary, #1f2733); }
		.pf-total-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.3rem 0.6rem; margin-bottom: 0.5rem; }
		.pf-total-grid > div { display: flex; justify-content: space-between; gap: 0.5rem; font-size: 0.78rem; }
		.pf-total-lbl { color: var(--theme-color-text-muted, #8a94a3); }
		.pf-total-val { font-variant-numeric: tabular-nums; color: var(--theme-color-text-primary, #1f2733); font-weight: 500; }
		.pf-bar { height: 7px; border-radius: 999px; background: var(--theme-color-background-secondary, #f0f2f6); overflow: hidden; }
		.pf-bar-fill { height: 100%; background: var(--theme-color-brand-primary, #156dd1); border-radius: 999px; }
		.pf-bar-fill.is-over { background: var(--theme-color-status-warning, #d99a2b); }
		.pf-bar-lbl { font-size: 0.7rem; color: var(--theme-color-text-muted, #8a94a3); margin-top: 0.25rem; }
		.pf-outcome-row { display: flex; flex-flow: row wrap; gap: 0.7rem 1.5rem; margin-top: 0.8rem; }
		.pf-outcome-row > .pf-outcomes { flex: 1 1 260px; }
		.pf-outcomes-head { font-size: 0.64rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-muted, #8a94a3); margin-bottom: 0.3rem; }
		.pf-outcome { display: flex; justify-content: space-between; font-size: 0.8rem; padding: 0.2rem 0; border-bottom: 1px solid var(--theme-color-border-light, #f0f2f6); font-variant-numeric: tabular-nums; }
		.pf-li-panel { margin-top: 0; }
		.pf-li-count { font-size: 0.78rem; color: var(--theme-color-text-muted, #8a94a3); font-weight: 500; }
		.pf-li-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
		.pf-li-table th { text-align: left; font-size: 0.64rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-muted, #8a94a3); padding: 0.4rem 0.6rem; border-bottom: 1px solid var(--theme-color-border-light, #e8ebf0); }
		.pf-li-table td { padding: 0.4rem 0.6rem; border-bottom: 1px solid var(--theme-color-border-light, #eef1f5); }
		.pf-li-code { font-variant-numeric: tabular-nums; color: var(--theme-color-text-secondary, #5b6673); white-space: nowrap; }
		.pf-li-name { color: var(--theme-color-text-primary, #1f2733); }
		.pf-li-row.is-uncat td { background: rgba(217, 154, 43, 0.06); }
		.pf-li-badge { margin-left: 0.5rem; font-size: 0.62rem; text-transform: uppercase; letter-spacing: 0.03em; color: var(--theme-color-status-warning, #d99a2b); border: 1px solid var(--theme-color-status-warning, #d99a2b); border-radius: 999px; padding: 0.05rem 0.4rem; }
		.pf-li-cat { width: 100%; max-width: 340px; padding: 0.25rem 0.4rem; border: 1px solid var(--theme-color-border-default, #d7dce3); border-radius: 6px; font: inherit; font-size: 0.82rem; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1f2733); }
		.pf-li-catcell { min-width: 220px; }
		.pf-li-pickmount { width: 100%; max-width: 340px; min-width: 200px; }
		.pf-li-note { text-align: left; margin: 0 0 0.6rem; }
		.pf-co-lock { display: flex; align-items: center; gap: 0.5rem; font-size: 0.86rem; color: #8a5a00; background: rgba(184,134,11,0.12); border: 1px solid rgba(184,134,11,0.25); border-radius: 7px; padding: 0.55rem 0.8rem; margin: 0 0 0.7rem; }

		/* Pick modal */
		.pf-modal-help { font-size: 0.86rem; color: var(--theme-color-text-secondary, #5b6673); margin: 0 0 0.6rem; }
		.pf-pick-list { max-height: 52vh; overflow-y: auto; margin-top: 0.6rem; border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 8px; }
		.pf-pick-row { display: flex; align-items: center; gap: 0.6rem; width: 100%; text-align: left; border: none; border-bottom: 1px solid var(--theme-color-border-light, #e8ebf0); background: var(--theme-color-background-panel, #fff); padding: 0.5rem 0.75rem; cursor: pointer; font: inherit; color: var(--theme-color-text-primary, #1f2733); }
		.pf-pick-row:last-child { border-bottom: none; }
		.pf-pick-row:hover { background: var(--theme-color-background-secondary, #f0f2f6); }
		.pf-pick-name { flex: 1 1 auto; font-size: 0.88rem; }
		.pf-pick-bal { font-size: 0.72rem; color: var(--theme-color-text-muted, #8a94a3); font-variant-numeric: tabular-nums; }
	`,

	Templates:
	[
		{
			Hash: 'ProjectFunding-Template',
			Template: html`
				<section class="section {~D:AppData.ProjectFunding.EmbedClass~}">
					<div class="pf-wrap">
						<div class="pf-head">
							<span class="pf-head-ic"><i class="fas fa-scale-balanced"></i></span>
							<div><h1 class="pf-title">{~D:AppData.ProjectFunding.TitleText~}</h1></div>
							{~TS:ProjectFunding-ReadOnly:AppData.ProjectFunding.ReadOnlySlot~}
							<span class="pf-head-spacer"></span>
							<span id="ProjectFunding-HeaderTools" class="pf-head-tools"></span>
							{~TS:ProjectFunding-ChangeProject:AppData.ProjectFunding.ChangeProjectSlot~}
						</div>
						{~TS:ProjectFunding-Loading:AppData.ProjectFunding.LoadingSlot~}
						{~TS:ProjectFunding-Error:AppData.ProjectFunding.ErrorSlot~}
						{~TS:ProjectFunding-Picker:AppData.ProjectFunding.PickerSlot~}
						{~TS:ProjectFunding-Body:AppData.ProjectFunding.BodySlot~}
					</div>
				</section>`,
		},
		{ Hash: 'ProjectFunding-ReadOnly', Template: html`<span class="pf-ro-pill" title="You can view this project’s funding. Configuring it needs the funding entitlement."><i class="fas fa-lock"></i> Read-only</span>` },
		{ Hash: 'ProjectFunding-ChangeProject', Template: html`<button type="button" class="pf-head-btn" onclick="_Pict.views.ProjectFunding.chooseAnotherProject()"><i class="fas fa-arrow-left"></i> Choose another project</button>` },
		{ Hash: 'ProjectFunding-Loading', Template: html`<div class="pf-state"><i class="fas fa-spinner fa-spin"></i> Loading funding…</div>` },
		{ Hash: 'ProjectFunding-Error', Template: html`<div class="pf-state"><i class="fas fa-triangle-exclamation"></i> {~D:Record.Message~}</div>` },
		{
			Hash: 'ProjectFunding-Picker',
			Template: html`
				<div class="pf-picker">
					<input id="ProjectFunding-ProjectFilter" class="pf-proj-filter" type="text" autocomplete="off" placeholder="Filter projects by name…" oninput="_Pict.views.ProjectFunding.onProjectFilter(this.value)" />
					<div id="ProjectFunding-ProjectList" class="pf-proj-list"><div class="pf-note"><i class="fas fa-spinner fa-spin"></i> Loading projects…</div></div>
				</div>`,
		},
		{
			Hash: 'ProjectFunding-Body',
			Template: html`
				<div class="pf-stack">
					<div class="pf-panel pf-acct-panel {~D:AppData.ProjectFunding.AcctClass~}">
						<div class="pf-panel-head pf-acct-head" role="button" tabindex="0" onclick="_Pict.views.ProjectFunding.toggleAccounting()"><i class="fas fa-chart-pie"></i> Accounting overview <span class="pf-head-spacer"></span><span class="pf-acct-caret icon"><i class="fas {~D:AppData.ProjectFunding.AcctChevron~}"></i></span></div>
						<div class="pf-panel-body pf-acct-body"><div id="ProjectFunding-Accounting"></div></div>
					</div>
					<div class="pf-panel">
						<div class="pf-panel-head"><i class="fas fa-sitemap"></i> Funding setup <span class="pf-head-spacer"></span>{~TS:ProjectFunding-AddCatBtn:AppData.ProjectFunding.ManageSlot~}</div>
						<div class="pf-panel-body"><div id="ProjectFunding-Grid"></div></div>
					</div>
					<div class="pf-panel pf-li-panel">
						<div class="pf-panel-head"><i class="fas fa-list-ol"></i> Line item funding <span class="pf-head-spacer"></span><span id="ProjectFunding-LICount" class="pf-li-count"></span></div>
						<div class="pf-panel-body"><div id="ProjectFunding-LineItems"></div></div>
					</div>
				</div>`,
		},
		{ Hash: 'ProjectFunding-AddCatBtn', Template: html`<button type="button" class="pf-tool-btn pf-tool-primary" onclick="_Pict.views.ProjectFunding.addCategory()"><i class="fas fa-plus"></i> Add category</button>` },
	],

	Renderables:
	[
		{ RenderableHash: 'ProjectFundingWrap', TemplateHash: 'ProjectFunding-Template', DestinationAddress: '#PlatformConfiguration-Application-Container' },
	],
};
